import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_ID,
  ErrorHandler,
  Injector,
  Provider,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { appRoutes } from './app-routes';
import { BillingScope } from './billing-consts';
import { CustomErrorHandlerService } from './error-handler';
import { GROWTHBOOK, gb } from './growthbook';
import { HANDLE_META } from './handle-meta';
import { PICFLOW_MAINTENANCE } from './maintenance';
import { NetworkInterceptor } from './network-interceptor';
import { PICFLOW_ANALYTICS } from './picflow-analytics';
import { PICFLOW_CHECKOUT, PicflowCheckout } from './picflow-checkout';
import { PICFLOW_CLERK } from './picflow-clerk';
import { PICFLOW_CONFIG } from './picflow-config';
import { PICFLOW_STORYBLOK } from './picflow-storyblok';
import { AppConfigService } from './providers/config.service';
import { routes } from './routes';
import { AnalyticsService } from './services/analytics.service';
import { BrowserService } from './services/browser.service';
import { GrowthBookExperimentService } from './services/growth-book-experiment.service';
import { LanguageService } from './services/language.service';
import { MaintenanceService } from './services/maintenance.service';
import { MetaService } from './services/meta.service';
import { NavigationHistoryService } from './services/navigation-history.service';
import { ScrollService } from './services/scroll.service';
import { StoryblokService } from './services/storyblok.service';
import { tenantRoutes } from './tenant-routes';

function initializeApp(
  scroll: ScrollService,
  an: AnalyticsService,
  meta: MetaService,
  config: AppConfigService,
  maintenance: MaintenanceService,
  languageService: LanguageService,
  browserService: BrowserService,
  navigationHistoryService: NavigationHistoryService,
  router: Router
) {
  return () => {
    const routes = getRoutes(browserService);
    if (routes) router.resetConfig(routes);
  };
}

function getRoutes(browserService: BrowserService) {
  const isTenantDomain = browserService.isTenantDomain();
  const isApp = browserService.isApp();
  if (isTenantDomain) return tenantRoutes;
  if (isApp) return appRoutes;
}

export const appConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()), // not using withFetch because it breaks the upload progress events
    provideClientHydration(withIncrementalHydration(), withEventReplay()),
    provideAnimations(),
    CookieService,
    { provide: APP_ID, useValue: 'picflow' },
    <Provider>{ provide: HTTP_INTERCEPTORS, useClass: NetworkInterceptor, multi: true },
    {
      provide: GROWTHBOOK,
      useFactory: (service: GrowthBookExperimentService) => gb(service.onExperimentViewed),
      deps: [GrowthBookExperimentService],
    },
    {
      provide: PICFLOW_MAINTENANCE,
      useFactory: (maintenance: MaintenanceService) => maintenance,
      deps: [MaintenanceService],
    },
    {
      provide: PICFLOW_CHECKOUT,
      useFactory: (injector: Injector) => {
        return <PicflowCheckout>{
          open: async (scope: BillingScope) => {
            const { CheckoutService } = await import('./services/checkout.service');
            injector.get(CheckoutService).open(scope);
          },
        };
      },
      deps: [Injector],
    },
    { provide: PICFLOW_CONFIG, useFactory: (config: AppConfigService) => config, deps: [AppConfigService] },
    { provide: PICFLOW_ANALYTICS, useFactory: (analytics: AnalyticsService) => analytics, deps: [AnalyticsService] },
    {
      provide: PICFLOW_CLERK,
      useFactory: (config: AppConfigService) => {
        return async () => {
          const { Clerk } = await import('@clerk/clerk-js');
          return new Clerk(config.clerkKey);
        };
      },
      deps: [AppConfigService],
    },
    { provide: HANDLE_META, useFactory: (meta: MetaService) => meta.handleMeta.bind(meta), deps: [MetaService] },
    { provide: PICFLOW_STORYBLOK, useFactory: (canny: StoryblokService) => canny, deps: [StoryblokService] },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(ScrollService),
        inject(AnalyticsService),
        inject(MetaService),
        inject(AppConfigService),
        inject(MaintenanceService),
        inject(LanguageService),
        inject(BrowserService),
        inject(NavigationHistoryService),
        inject(Router)
      );
      return initializerFn();
    }),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideZoneChangeDetection({
      runCoalescing: true,
      eventCoalescing: true,
    }),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    // [{ provide: LOCALE_ID, useValue: 'fr' }],
  ],
};
